/**
 * Welded mesh graph and Laplacian smoothing — ADR-028 E3's delta-mush
 * foundation.
 *
 * Delta mush needs a SMOOTHED version of the mesh, at rest and at pose. Two
 * things make that harder than "average each vertex with its neighbours":
 *
 * 1. **UV seams split vertices.** A rendered mesh duplicates a vertex wherever
 *    a UV, normal or material seam crosses it — two array entries at the same
 *    position, with no shared index. Smoothed independently, the copies see
 *    different neighbours, drift apart, and the seam opens as a visible crack
 *    exactly where the crease is most looked at. So the graph is built on
 *    WELDED positions: copies share one node, smooth as one, and scatter back
 *    identically by construction.
 * 2. **Open borders shrink.** Uniform (umbrella) smoothing pulls a boundary
 *    vertex toward the mesh interior, so a mesh cut at the wrist or neck
 *    retreats from its own edge a little more each iteration. Boundary nodes —
 *    those on an edge used by a single triangle — are therefore PINNED. A
 *    closed body has almost none; a garment or a cut limb is all border, and
 *    silently eating it would be the kind of damage nobody attributes to the
 *    crease pass.
 *
 * Pure arrays in, pure arrays out: no three types, no geometry mutation.
 *
 * Depends on: nothing. Depended on by: the crease derivation.
 */

export interface WeldedGraph {
  /** vertex index → welded node id. */
  readonly weldOf: Int32Array;
  readonly nodeCount: number;
  /** welded node id → its welded neighbours. */
  readonly neighbors: readonly Int32Array[];
  /** Nodes on an open border, pinned during smoothing. */
  readonly boundary: Uint8Array;
}

export class MeshGraphError extends Error {}

/**
 * Quantisation step for welding, as a fraction of the mesh's bounding-box
 * diagonal. Two positions inside this are the same point. Loose enough to
 * catch exporter float drift across a seam, far tighter than any real edge.
 */
const WELD_FRACTION = 1e-6;

/**
 * Build the welded adjacency graph of an indexed triangle mesh.
 *
 * `index` may be null for a non-indexed mesh, in which case triangles are
 * consecutive vertex triples — welding is what makes that usable at all, since
 * a non-indexed mesh shares nothing.
 */
export function buildWeldedGraph(positions: Float32Array, index: ArrayLike<number> | null): WeldedGraph {
  const vertexCount = positions.length / 3;
  if (!Number.isInteger(vertexCount) || vertexCount === 0) {
    throw new MeshGraphError(`positions must be a non-empty multiple of 3, got length ${positions.length}`);
  }
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let v = 0; v < vertexCount; v += 1) {
    const x = positions[v * 3]!, y = positions[v * 3 + 1]!, z = positions[v * 3 + 2]!;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      throw new MeshGraphError(`vertex ${v} has a non-finite position`);
    }
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  const diagonal = Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
  const step = Math.max(diagonal * WELD_FRACTION, Number.MIN_VALUE);

  const weldOf = new Int32Array(vertexCount);
  const byKey = new Map<string, number>();
  let nodeCount = 0;
  for (let v = 0; v < vertexCount; v += 1) {
    const key = `${Math.round(positions[v * 3]! / step)},${Math.round(positions[v * 3 + 1]! / step)},${Math.round(positions[v * 3 + 2]! / step)}`;
    let node = byKey.get(key);
    if (node === undefined) {
      node = nodeCount;
      nodeCount += 1;
      byKey.set(key, node);
    }
    weldOf[v] = node;
  }

  const triangles = index === null ? vertexCount : index.length;
  if (triangles % 3 !== 0) throw new MeshGraphError(`triangle list is not a multiple of 3 (${triangles})`);
  const sets: Set<number>[] = Array.from({ length: nodeCount }, () => new Set<number>());
  // An edge's use count decides the border: one triangle means open.
  const edgeUses = new Map<number, number>();
  const at = (i: number): number => weldOf[index === null ? i : index[i]!]!;
  for (let t = 0; t < triangles; t += 3) {
    const a = at(t), b = at(t + 1), c = at(t + 2);
    for (const [p, q] of [[a, b], [b, c], [c, a]] as const) {
      if (p === q) continue; // a degenerate triangle names no edge
      sets[p]!.add(q);
      sets[q]!.add(p);
      const lo = Math.min(p, q), hi = Math.max(p, q);
      const key = lo * nodeCount + hi;
      edgeUses.set(key, (edgeUses.get(key) ?? 0) + 1);
    }
  }
  const boundary = new Uint8Array(nodeCount);
  for (const [key, uses] of edgeUses) {
    if (uses !== 1) continue;
    boundary[Math.floor(key / nodeCount)] = 1;
    boundary[key % nodeCount] = 1;
  }
  const neighbors = sets.map((s) => Int32Array.from(s).sort());
  return { weldOf, nodeCount, neighbors, boundary };
}

/**
 * Laplacian (umbrella) smoothing on the welded graph, scattered back per
 * vertex. `lambda` is the step toward the neighbour average, `iterations` the
 * count; boundary and isolated nodes hold still.
 *
 * Returns a NEW array — the caller's positions are never touched, because both
 * the smoothed and the original are needed to record the detail offset.
 */
export function smoothWelded(
  graph: WeldedGraph,
  positions: Float32Array,
  iterations: number,
  lambda: number,
): Float32Array {
  if (!Number.isInteger(iterations) || iterations < 0) throw new MeshGraphError(`iterations must be a non-negative integer, got ${iterations}`);
  if (!(lambda >= 0 && lambda <= 1)) throw new MeshGraphError(`lambda must be within 0..1, got ${lambda}`);
  return smoothWeldedSchedule(graph, positions, iterations, [lambda]);
}

/**
 * Taubin λ|μ smoothing — the shrinkage cure.
 *
 * Plain Laplacian smoothing is a low-pass filter whose pass-band gain is
 * below 1, so every iteration SHRINKS the mesh a little; sixty of them shrink
 * a tube's girth by a measurable fraction, and ADR-028 E3's 135° diagnosis
 * (2026-08-27) found exactly that: the crease reference at deep bends was
 * smaller than the flesh it was meant to restore, so the stage "corrected"
 * toward a shrunken target and made deep bends worse.
 *
 * Taubin alternates a shrinking step (λ > 0) with an inflating step (μ < 0,
 * |μ| slightly larger than λ). The pair's transfer function has gain ~1 across
 * the pass-band — low frequencies are preserved, high frequencies still
 * damped — so the surface is smoothed WITHOUT losing volume. Classic values
 * λ = 0.5, μ = −0.53 (Taubin 1995). The constraint μ < −λ is what puts the
 * pass-band edge at a positive frequency; violate it and this is just a
 * noisier Laplacian.
 */
export function smoothWeldedTaubin(
  graph: WeldedGraph,
  positions: Float32Array,
  iterations: number,
  lambda: number,
  mu: number,
): Float32Array {
  if (!Number.isInteger(iterations) || iterations < 0) throw new MeshGraphError(`iterations must be a non-negative integer, got ${iterations}`);
  if (!(lambda > 0 && lambda <= 1)) throw new MeshGraphError(`lambda must be within (0, 1], got ${lambda}`);
  if (!(mu < -lambda)) throw new MeshGraphError(`mu must be below -lambda (${-lambda}) for a pass-band to exist, got ${mu}`);
  return smoothWeldedSchedule(graph, positions, iterations, [lambda, mu]);
}

/**
 * One iteration applies each factor of `schedule` in turn as a Laplacian
 * step: `x += factor · (ringMean − x)`. A single positive factor is ordinary
 * smoothing; a [λ, μ] pair with μ < −λ is Taubin. Boundary and ringless
 * nodes are pinned in every step, as before.
 */
function smoothWeldedSchedule(
  graph: WeldedGraph,
  positions: Float32Array,
  iterations: number,
  schedule: readonly number[],
): Float32Array {
  const { weldOf, nodeCount, neighbors, boundary } = graph;
  const vertexCount = positions.length / 3;

  // Gather to nodes first: welded copies must enter as ONE value or the
  // averaging quietly weights a seam by how many copies it happens to have.
  let current = new Float64Array(nodeCount * 3);
  const seen = new Uint8Array(nodeCount);
  for (let v = 0; v < vertexCount; v += 1) {
    const n = weldOf[v]!;
    if (seen[n] === 1) continue;
    seen[n] = 1;
    current[n * 3] = positions[v * 3]!;
    current[n * 3 + 1] = positions[v * 3 + 1]!;
    current[n * 3 + 2] = positions[v * 3 + 2]!;
  }
  let next = new Float64Array(nodeCount * 3);
  for (let step = 0; step < iterations; step += 1) {
    for (const factor of schedule) {
      for (let n = 0; n < nodeCount; n += 1) {
        const ring = neighbors[n]!;
        if (ring.length === 0 || boundary[n] === 1) {
          next[n * 3] = current[n * 3]!;
          next[n * 3 + 1] = current[n * 3 + 1]!;
          next[n * 3 + 2] = current[n * 3 + 2]!;
          continue;
        }
        let sx = 0, sy = 0, sz = 0;
        for (const m of ring) {
          sx += current[m * 3]!;
          sy += current[m * 3 + 1]!;
          sz += current[m * 3 + 2]!;
        }
        const inv = 1 / ring.length;
        next[n * 3] = current[n * 3]! + factor * (sx * inv - current[n * 3]!);
        next[n * 3 + 1] = current[n * 3 + 1]! + factor * (sy * inv - current[n * 3 + 1]!);
        next[n * 3 + 2] = current[n * 3 + 2]! + factor * (sz * inv - current[n * 3 + 2]!);
      }
      const swap = current;
      current = next;
      next = swap;
    }
  }

  const out = new Float32Array(positions.length);
  for (let v = 0; v < vertexCount; v += 1) {
    const n = weldOf[v]!;
    out[v * 3] = current[n * 3]!;
    out[v * 3 + 1] = current[n * 3 + 1]!;
    out[v * 3 + 2] = current[n * 3 + 2]!;
  }
  return out;
}
